import React, { useMemo } from "react";
import PropTypes from "prop-types";
import * as yup from "yup";
import { Button, Col, Form, message, Drawer, Row, Select } from "antd";
import { useGetProjectHotels, useReplicateHotelReports } from "../../hooks";

const { Option } = Select;

const ReplicateReports = ({
  reportModalOpen,
  setReportModalOPen,
  hotel,
  projectId,
  pmsId,
}) => {
  const { data, isLoading } = useGetProjectHotels(projectId, {
    enabled: !!projectId,
    select: (queryData) => {
      return queryData.filter(
        (hotelData) => !["Lost", "Sold"].includes(hotelData.hotel_status),
      );
    },
  });

  const filteredHotelList = useMemo(() => {
    if (Array.isArray(data)) {
      if (pmsId === "all") {
        return data;
      }
      return data.filter((hotelrecord) => {
        const hotelPmsList = hotelrecord.pms.map(
          (pmsrecord) => pmsrecord.pms_id._id,
        );
        return hotelPmsList.includes(pmsId);
      });
    }
    return [];
  }, [data, pmsId]);

  const { mutate: replicateHotelReport, isLoading: isReportsReplicating } =
    useReplicateHotelReports({
      onError: (error) => {
        message.error(error.response?.data?.message ?? "Server Error");
      },
      onSuccess: () => {
        message.success("Report updated successfully");
        setReportModalOPen(false);
      },
    });

  const replicateSubmit = (values) => {
    if (values?.replicateTo) {
      for (let i = 0; i < values.replicateTo.length; i += 1) {
        replicateHotelReport({
          projectId,
          sourceHotelId: hotel,
          destinationHotelId: values.replicateTo[i],
        });
      }
    }
  };

  const onCancelHandler = () => {
    setReportModalOPen(false);
  };

  const schema = yup.object().shape({
    hotel: yup.string().required(),
    replicateTo: yup
      .array()
      .of(yup.string())
      .min(1)
      .required("Replicate To field is required"),
  });

  const yupSync = {
    async validator({ field }, value) {
      schema.validateSyncAt(field, { [field]: value });
    },
  };
  return (
    <Drawer
      title="Replicate Reports"
      open={reportModalOpen}
      footer={null}
      onClose={onCancelHandler}
    >
      <Form layout="vertical" onFinish={replicateSubmit}>
        <Form.Item label="Replicate From">
          <Select value={hotel} disabled>
            {Array.isArray(filteredHotelList) &&
              filteredHotelList.map((filteredHotel) => (
                <Option value={filteredHotel._id} key={filteredHotel._id}>
                  {`${filteredHotel.hotel_code} - ${filteredHotel.hotel_name}`}
                </Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item label="Replicate To" name="replicateTo" rules={[yupSync]}>
          <Select
            placeholder="Replicate To"
            disabled={!hotel}
            mode="multiple"
            showSearch
            optionFilterProp="children"
            loading={isLoading}
            filterOption={(input, option) => {
              return (
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              );
            }}
          >
            {Array.isArray(filteredHotelList) &&
              filteredHotelList
                .filter((replicateHotel) => hotel !== replicateHotel._id)
                ?.map((hotel) => (
                  <Option value={hotel._id} key={hotel._id}>
                    {`${hotel.hotel_code} - ${hotel.hotel_name}`}
                  </Option>
                ))}
          </Select>
        </Form.Item>
        <Row justify="space-between" gutter={12}>
          <Col span={12}>
            <Form.Item>
              <Button block type="ghost" onClick={onCancelHandler}>
                Cancel
              </Button>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isReportsReplicating}
              >
                Replicate
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
};

ReplicateReports.defaultProps = {
  reportModalOpen: false,
  setReportModalOPen: () => {},
  hotel: undefined,
  projectId: undefined,
  pmsId: undefined,
};

ReplicateReports.propTypes = {
  reportModalOpen: PropTypes.bool,
  setReportModalOPen: PropTypes.func,
  hotel: PropTypes.string,
  projectId: PropTypes.string,
  pmsId: PropTypes.string,
};
export default ReplicateReports;
